import { useState } from "react";
import { motion } from "framer-motion";
import { Building2, Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "@/lib/api";

type Permission = string;

type LoginResponse = {
  allPermission: Permission[];
  accessToken: string;
  password_expired: boolean,
  userData: { id: number; role_id: number; name: string; email: string; username?: string; role?: string };
};

export default function LoginPage() {
  const [login, setLogin] = useState("admin"); // <-- email or username
  const [password, setPassword] = useState("12345678");
  const [rememberMe, setRememberMe] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const data = await apiFetch<LoginResponse>("/auth/login", {
        method: "POST",
        body: JSON.stringify({
          login, // <-- send login instead of email
          password,
          remember_me: rememberMe,
        }),
      });

      

      if (data.password_expired) {
        navigate("/set-password", {
    state: { login }, // ✅ pass login here
  }); //send email or username,,, 
      } else {
        const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem("accessToken", data.accessToken);
      storage.setItem("userData", JSON.stringify(data.userData));
      storage.setItem("allPermission", JSON.stringify(data.allPermission));
        navigate("/home");
      }
    } catch (e: any) {
      setError(e.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden px-4">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-card border border-border rounded-2xl p-8 shadow-card">
          <div className="flex flex-col items-center mb-6">
            <div className="h-16 w-16 rounded-xl gradient-primary flex items-center justify-center mb-4">
              <Building2 className="h-8 w-8 text-primary-foreground" />
            </div>
            <h1 className="text-2xl font-bold">Welcome to Seanala 👋</h1>
          </div>

          {error && (
            <div className="mb-4 text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label>Email or Username</Label>
              <Input
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                disabled={loading}
                placeholder="Enter email or username"
              />
            </div>

            <div>
              <Label>Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  placeholder="Password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                checked={rememberMe}
                onCheckedChange={(v) => setRememberMe(Boolean(v))}
              />
              <span className="text-sm">Remember me</span>
            </div>

            <Button type="submit" className="w-full h-12" disabled={loading}>
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="animate-spin h-4 w-4" />
                  Logging in...
                </span>
              ) : (
                "Login"
              )}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}